import React, { isValidElement } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { toast } from "sonner";
import { ROLE_HIERARCHY, type Role } from '@shared/const';

type RoleType = 'operator' | 'supervisor' | 'admin';

interface RoleProtectedRouteProps {
  minRole: RoleType;
  children: React.ReactNode;
}

/**
 * Role-based access control wrapper for route components.
 * Only checks permissions when the route is actually active to prevent toast spam.
 *
 * @param minRole - Minimum role required ('operator' | 'supervisor' | 'admin')
 * @param children - Route component(s) to render if user has sufficient role
 */
export default function RoleProtectedRoute({ minRole, children }: RoleProtectedRouteProps) {
  const { user, isAuthenticated, loading } = useAuth();
  const [location, setLocation] = useLocation();
  
  // Extract the path from the child Route component
  let routePath: string | undefined;
  if (isValidElement(children)) {
    const childProps = children.props as any;
    if (childProps && typeof childProps === 'object' && 'path' in childProps) {
      routePath = childProps.path as string;
    }
  }

  useEffect(() => {
    // Skip checks if:
    // - Auth is loading
    // - No user or not authenticated  
    // - User role not yet loaded
    // - We don't know what route this is protecting
    // - Current location doesn't match this route's path
    if (loading || !user || !isAuthenticated || !user.role || !routePath) {
      return;
    }

    // Only check if we're actually ON this route
    // This prevents all routes from firing their guards on initial render
    const isOnThisRoute = location === routePath || location.startsWith(routePath + '/');
    if (!isOnThisRoute) {
      return;
    }

    const userLevel = ROLE_HIERARCHY[user.role as Role] ?? 0;
    const minLevel = ROLE_HIERARCHY[minRole as Role];

    if (userLevel < minLevel) {
      toast.error("Access denied", {
        description: `You need ${minRole} access or higher to view this page.`,
      });
      setLocation("/", { replace: true });
    }
  }, [user, isAuthenticated, loading, minRole, location, setLocation, routePath]);

  return <>{children}</>;
}
